

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, BookOpen, ArrowRight } from "lucide-react";

/**
 * „Nauja tema!" — the announcement, and only the announcement.
 *
 * It used to lead with „Pažiūrėti teoriją", which opened the panel for the child.
 * That is the one thing this dialog should not do: theory opened *for* somebody is
 * theory they scroll past (measured at 24% engagement when a new topic auto-opened
 * it), and it puts a wall of prose between them and the question before they have
 * met the question. So the dialog names the topic and gets out of the way; the
 * theory panel does the asking itself, by pulsing until it is opened or the child
 * gets a question of the topic right (`.theory-nudge` in globals.css).
 */
type NewSkillModalProps = {
  isOpen: boolean;
  onClose: () => void;
  skillName: string;
  skillDescription?: string;
};

export function NewSkillModal({
  isOpen,
  onClose,
  skillName,
  skillDescription,
}: NewSkillModalProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay for animation
      const timer = setTimeout(() => setShow(true), 100);
      return () => clearTimeout(timer);
    }
    setShow(false);
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span className="text-3xl animate-bounce">🌟</span>
            Nauja tema!
          </DialogTitle>
          <DialogDescription className="sr-only">
            Informacija apie naują temą
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <div
            className={`text-center transition-all duration-500 ${
              show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cyan-100 to-blue-200 dark:from-cyan-900/30 dark:to-blue-800/30 mb-4">
              <Sparkles className="h-8 w-8 text-cyan-600 dark:text-cyan-400" />
            </div>
            <h3 className="text-2xl font-bold text-cyan-700 dark:text-cyan-300 mb-2">
              {skillName}
            </h3>
            {skillDescription && (
              <p className="text-muted-foreground">{skillDescription}</p>
            )}
          </div>

          <div
            className={`p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 transition-all duration-500 delay-200 ${
              show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              {/* Points at the pulsing panel rather than opening it. „Kai
                  prireiks", not „prieš pradedant": a child who wants to try the
                  question first is doing the right thing, and the theory is one
                  tap away for the whole session. */}
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Patarimas</p>
                <p>
                  Viršuje mirksi <strong>Teorija</strong> — atsidaryk, kai
                  prireiks. Ji lieka po ranka visą laiką!
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="sm:flex-col sm:space-x-0 gap-2">
          <Button onClick={onClose} className="w-full gap-2">
            Pradėti spręsti
            <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
